/**
 * @file: useDashboardHeader.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { DashboardHeaderSegment } from '@euler/app/flows/home/types';
import { OrgUserRoleHelper } from '@euler/functions/OrgUserRoleHelper';
import { useStoreConfiguration } from '@euler/functions/useStoreConfiguration';
import { useCurrentUserRole } from '@euler/functions/user/useCurrentUserRole';
import { useMemo, useState } from 'react';

export const useDashboardHeader = () => {
  const storeConfiguration = useStoreConfiguration();
  const role = useCurrentUserRole();

  const segmentList = useMemo<DashboardHeaderSegment[]>(() => {
    const roleHelper = new OrgUserRoleHelper(role);
    const hasStoreLevelTaskAccess =
      !storeConfiguration.restrictedRolesForStoreLevelTasks?.length ||
      roleHelper.matchesAny(
        storeConfiguration.restrictedRolesForStoreLevelTasks,
      );
    const list: DashboardHeaderSegment[] = [
      {
        type: 'personal',
        name: '个人',
      },
    ];
    if (hasStoreLevelTaskAccess) {
      list.unshift({
        type: 'store',
        name: '门店',
      });
    }
    return list;
  }, [role, storeConfiguration.restrictedRolesForStoreLevelTasks]);

  const segments = useMemo(() => segmentList.map(x => x.name), [segmentList]);

  const tags = storeConfiguration.app?.orderTagsForNav;

  const [_segmentIndex, setSegmentIndex] = useState(0);
  const [activeTag, setActiveTag] = useState<string>();

  const segmentIndex = Math.min(_segmentIndex, segmentList.length - 1);

  const assignedToMe = segmentList[segmentIndex]?.type === 'personal';

  return {
    segmentList,
    segments,
    tags,
    assignedToMe,
    segmentIndex,
    setSegmentIndex,
    activeTag,
    setActiveTag,
  };
};
